// Structured identification facts, promoted out of the presentation layer.
//
// Every value here is a machine-readable restatement of prose that already exists in
// src/lib/species.ts under that species' own sourceIds — nothing is estimated and
// nothing new is claimed. tick-id.test.ts re-derives the millimetre bounds from the
// `sizeByStage` sentences and fails if the two ever drift apart.
//
// The trait vocabulary is deliberately small. It covers only what a person can see
// with the naked eye while holding a tick or looking at one on their skin. Mouthpart
// length, festoons, basis-capituli shape and spiracular goblet counts are all real
// discriminators documented in `identMarks`, and all of them are useless to someone
// in a parking lot with a jar — so they stay prose on the species page and never
// become questions here.

export type LifeStage = "larva" | "nymph" | "adult";

export const LIFE_STAGES: readonly LifeStage[] = ["larva", "nymph", "adult"] as const;

/**
 * A size in millimetres, as published. Both bounds are optional because the sources
 * are not uniform: some give a range ("3–4 mm"), some only a ceiling ("under ~1 mm"),
 * some only a floor ("10 mm or more"). Inventing the missing end would be exactly the
 * false precision this module exists to avoid.
 */
export interface SizeMm {
  minMm?: number;
  maxMm?: number;
}

/** The single number to draw when rendering this size at actual scale. */
export function typicalMm(size: SizeMm): number {
  if (size.minMm !== undefined && size.maxMm !== undefined) return (size.minMm + size.maxMm) / 2;
  return size.minMm ?? size.maxMm ?? 0;
}

export function formatMm(size: SizeMm): string {
  if (size.minMm !== undefined && size.maxMm !== undefined) {
    return size.minMm === size.maxMm ? `${size.minMm} mm` : `${size.minMm}–${size.maxMm} mm`;
  }
  if (size.maxMm !== undefined) return `under ${size.maxMm} mm`;
  if (size.minMm !== undefined) return `${size.minMm} mm or more`;
  return "size not published";
}

/**
 * What the back of the tick looks like. These are the five things people actually
 * describe, and between them they separate the eight profiled species about as far as
 * unaided sight can.
 */
export type BackPattern =
  | "single-white-dot"
  | "scattered-white-flecks"
  | "solid-dark-shield"
  | "marbled-silver"
  | "silver-stripes"
  | "plain-no-markings";

export type LegTone = "dark-contrasting" | "same-as-body";

/** Where the tick was found. The brown dog tick's own identMarks call this "a clue". */
export type FoundContext = "outdoors" | "on-a-dog" | "indoors";

export interface SpeciesIdProfile {
  /** Joins src/lib/species.ts by slug. */
  slug: string;
  /** Mirrors that species' commonName, so interactive flows need not load species.ts. */
  commonName: string;
  /**
   * The diseases this species actually transmits, mirroring `diseasesCarried`. This is
   * what lets the bite timeline show someone the windows for the tick that bit them
   * instead of every tickborne illness in the country. An empty list is meaningful:
   * the Gulf Coast tick's main illness has no page here, and no human illness has been
   * confirmed from an Asian longhorned tick bite in the US.
   */
  diseaseSlugs: readonly string[];
  /** Unfed length by stage. */
  stages: Record<LifeStage, SizeMm>;
  /** How big a fully fed adult female gets — engorgement changes everything. */
  engorgedFemale: SizeMm;
  /** Every back pattern this species presents across sexes and stages. */
  patterns: readonly BackPattern[];
  legTone: LegTone;
  contexts: readonly FoundContext[];
  /**
   * Species that cannot be told apart from this one by eye, by their own published
   * identification notes. Surfaced verbatim rather than silently broken by a tiebreak.
   */
  notSeparableFrom?: readonly string[];
}

export const LEG_COUNT: Record<LifeStage, 6 | 8> = {
  larva: 6,
  nymph: 8,
  adult: 8,
};

export const STAGE_LABEL: Record<LifeStage, string> = {
  larva: "Larva",
  nymph: "Nymph",
  adult: "Adult",
};

export const PATTERN_LABEL: Record<BackPattern, string> = {
  "single-white-dot": "One white dot in the middle of the back",
  "scattered-white-flecks": "Scattered white flecks or streaks near the rear edge",
  "solid-dark-shield": "A plain dark shield with an orange-red body behind it",
  "marbled-silver": "Mottled silver-grey marbling",
  "silver-stripes": "Silvery-white stripes or a network of lines",
  "plain-no-markings": "Plain: no dot, stripe or pattern",
};

export const LEG_TONE_LABEL: Record<LegTone, string> = {
  "dark-contrasting": "Noticeably darker than the body",
  "same-as-body": "About the same colour as the body",
};

export const CONTEXT_LABEL: Record<FoundContext, string> = {
  outdoors: "On a person, outdoors",
  "on-a-dog": "On a dog",
  indoors: "Indoors, in the house or kennel",
};

/**
 * The profiles. Read alongside `sizeByStage` and `identMarks` in src/lib/species.ts —
 * each field below is that species' own published description, transcribed.
 */
export const ID_PROFILES: readonly SpeciesIdProfile[] = [
  {
    slug: "lone-star-tick",
    commonName: "Lone Star tick",
    diseaseSlugs: [
      "ehrlichiosis",
      "stari",
      "heartland-virus",
      "bourbon-virus",
      "tularemia",
      "alpha-gal-syndrome",
    ],
    stages: {
      larva: { minMm: 0.5, maxMm: 0.5 },
      nymph: { minMm: 1, maxMm: 1.5 },
      adult: { minMm: 3, maxMm: 4 },
    },
    engorgedFemale: { minMm: 10, maxMm: 16 },
    patterns: ["single-white-dot", "scattered-white-flecks", "plain-no-markings"],
    legTone: "same-as-body",
    contexts: ["outdoors", "on-a-dog"],
  },
  {
    slug: "blacklegged-tick",
    commonName: "Blacklegged tick",
    diseaseSlugs: ["lyme-disease", "anaplasmosis", "babesiosis", "powassan-virus", "ehrlichiosis"],
    stages: {
      larva: { maxMm: 1 },
      nymph: { minMm: 1.3, maxMm: 1.7 },
      adult: { minMm: 2, maxMm: 3.7 },
    },
    engorgedFemale: { minMm: 10, maxMm: 10 },
    patterns: ["solid-dark-shield", "plain-no-markings"],
    legTone: "dark-contrasting",
    contexts: ["outdoors"],
    notSeparableFrom: ["western-blacklegged-tick"],
  },
  {
    slug: "western-blacklegged-tick",
    commonName: "Western blacklegged tick",
    diseaseSlugs: ["lyme-disease", "anaplasmosis"],
    stages: {
      larva: { minMm: 0.5, maxMm: 0.5 },
      nymph: { minMm: 1, maxMm: 2 },
      adult: { minMm: 2, maxMm: 3 },
    },
    engorgedFemale: { minMm: 10 },
    patterns: ["solid-dark-shield", "plain-no-markings"],
    legTone: "dark-contrasting",
    contexts: ["outdoors"],
    notSeparableFrom: ["blacklegged-tick"],
  },
  {
    slug: "american-dog-tick",
    commonName: "American dog tick",
    diseaseSlugs: ["rocky-mountain-spotted-fever", "tularemia"],
    stages: {
      larva: { minMm: 0.6, maxMm: 0.6 },
      nymph: { minMm: 0.9, maxMm: 0.9 },
      adult: { minMm: 3.6, maxMm: 5 },
    },
    engorgedFemale: { minMm: 10, maxMm: 15 },
    patterns: ["marbled-silver"],
    legTone: "same-as-body",
    contexts: ["outdoors", "on-a-dog"],
    notSeparableFrom: ["rocky-mountain-wood-tick"],
  },
  {
    slug: "brown-dog-tick",
    commonName: "Brown dog tick",
    diseaseSlugs: ["rocky-mountain-spotted-fever"],
    stages: {
      larva: { minMm: 0.5, maxMm: 0.5 },
      nymph: { minMm: 1.1, maxMm: 1.3 },
      adult: { minMm: 2.3, maxMm: 3.2 },
    },
    engorgedFemale: { maxMm: 12 },
    patterns: ["plain-no-markings"],
    legTone: "same-as-body",
    contexts: ["on-a-dog", "indoors"],
  },
  {
    slug: "gulf-coast-tick",
    commonName: "Gulf Coast tick",
    diseaseSlugs: [],
    stages: {
      larva: { minMm: 0.5, maxMm: 0.5 },
      nymph: { minMm: 1.3, maxMm: 1.3 },
      adult: { minMm: 6, maxMm: 6 },
    },
    engorgedFemale: { minMm: 10 },
    patterns: ["silver-stripes", "plain-no-markings"],
    legTone: "same-as-body",
    contexts: ["outdoors", "on-a-dog"],
  },
  {
    slug: "rocky-mountain-wood-tick",
    commonName: "Rocky Mountain wood tick",
    diseaseSlugs: ["rocky-mountain-spotted-fever", "tularemia"],
    stages: {
      larva: { minMm: 0.5, maxMm: 0.5 },
      nymph: { minMm: 1, maxMm: 1.5 },
      adult: { minMm: 3, maxMm: 5 },
    },
    engorgedFemale: { minMm: 13, maxMm: 16 },
    patterns: ["marbled-silver"],
    legTone: "same-as-body",
    contexts: ["outdoors", "on-a-dog"],
    notSeparableFrom: ["american-dog-tick"],
  },
  {
    slug: "asian-longhorned-tick",
    commonName: "Asian longhorned tick",
    diseaseSlugs: [],
    stages: {
      larva: { maxMm: 1 },
      nymph: { minMm: 1.5, maxMm: 1.5 },
      adult: { minMm: 2, maxMm: 2.6 },
    },
    engorgedFemale: { minMm: 9, maxMm: 10 },
    patterns: ["plain-no-markings"],
    legTone: "same-as-body",
    contexts: ["outdoors", "on-a-dog"],
  },
];

const BY_SLUG = new Map(ID_PROFILES.map((p) => [p.slug, p]));

export function getIdProfile(slug: string): SpeciesIdProfile | undefined {
  return BY_SLUG.get(slug);
}

/** The size to compare against, given what the user told us about feeding state. */
export function sizeFor(profile: SpeciesIdProfile, stage: LifeStage, engorged: boolean): SizeMm {
  return engorged && stage === "adult" ? profile.engorgedFemale : profile.stages[stage];
}

// ---------------------------------------------------------------------------
// Narrowing
//
// This ranks the profiled species against what someone says they can see. It is a
// narrowing aid, never a determination: nothing is eliminated, the output is a count
// of matched features rather than a confidence score, and `caveats` carries the
// conditions under which the whole exercise is unreliable. Callers must display them.
// ---------------------------------------------------------------------------

export interface IdAnswers {
  /** Measured against the on-screen ruler, in millimetres. */
  sizeMm?: number;
  /** Has it fed? A fed tick loses its markings. */
  engorged?: boolean;
  pattern?: BackPattern;
  legTone?: LegTone;
  context?: FoundContext;
}

export interface IdMatch {
  slug: string;
  matched: number;
  matchedOn: string[];
  missedOn: string[];
  notSeparableFrom: readonly string[];
}

export interface IdResult {
  /** All eight profiled species, best match first. Nothing is ever excluded. */
  matches: IdMatch[];
  /** How many questions the person actually answered. */
  answered: number;
  /** The size band the measurement falls in, when one was given. */
  impliedStage?: LifeStage;
  /** Conditions that undermine the result. Always render these. */
  caveats: string[];
}

/** People eyeball size, so the bands are generous. Better a long list than a wrong one. */
const SIZE_TOLERANCE = 0.4;

function sizeMatches(size: SizeMm, mm: number): boolean {
  const lo = (size.minMm ?? 0) * (1 - SIZE_TOLERANCE);
  const hi =
    size.maxMm === undefined ? Number.POSITIVE_INFINITY : size.maxMm * (1 + SIZE_TOLERANCE);
  return mm >= lo && mm <= hi;
}

/**
 * Which life stage a measurement lands in, across every profiled species. Used for
 * labelling only — the narrowing itself compares against each species' own bands.
 */
export function stageForSize(mm: number): LifeStage {
  if (mm <= 1) return "larva";
  if (mm <= 2) return "nymph";
  return "adult";
}

export function identify(answers: IdAnswers): IdResult {
  const answered = [
    answers.sizeMm !== undefined,
    answers.pattern !== undefined,
    answers.legTone !== undefined,
    answers.context !== undefined,
  ].filter(Boolean).length;

  const engorged = answers.engorged ?? false;
  const impliedStage = answers.sizeMm === undefined ? undefined : stageForSize(answers.sizeMm);

  const matches: IdMatch[] = ID_PROFILES.map((profile) => {
    const matchedOn: string[] = [];
    const missedOn: string[] = [];

    if (answers.sizeMm !== undefined) {
      const hit = engorged
        ? sizeMatches(profile.engorgedFemale, answers.sizeMm)
        : LIFE_STAGES.some((stage) => sizeMatches(profile.stages[stage], answers.sizeMm as number));
      (hit ? matchedOn : missedOn).push("size");
    }
    // Markings are meaningless on a fed tick, so they are not scored against it.
    if (answers.pattern !== undefined && !engorged) {
      (profile.patterns.includes(answers.pattern) ? matchedOn : missedOn).push("pattern");
    }
    if (answers.legTone !== undefined) {
      (profile.legTone === answers.legTone ? matchedOn : missedOn).push("legs");
    }
    if (answers.context !== undefined) {
      (profile.contexts.includes(answers.context) ? matchedOn : missedOn).push("context");
    }

    return {
      slug: profile.slug,
      matched: matchedOn.length,
      matchedOn,
      missedOn,
      notSeparableFrom: profile.notSeparableFrom ?? [],
    };
  }).sort((a, b) => b.matched - a.matched || a.slug.localeCompare(b.slug));

  const caveats: string[] = [];
  if (engorged) {
    caveats.push(
      "A tick that has fed swells and darkens, and its markings stretch out or disappear. Colour and pattern were left out of this comparison, and size means much less than usual.",
    );
  }
  if (impliedStage === "larva") {
    caveats.push(
      "At this size the profiled species look essentially identical without a microscope. Treat everything below as a list of possibilities, not a shortlist.",
    );
  }
  const top = matches[0]?.matched ?? 0;
  const tied = matches.filter((m) => m.matched === top).map((m) => m.slug);
  // A pair is one fact, so key on the unordered pair — otherwise the same caveat is
  // stated twice with the names swapped round.
  const namedPairs = new Set<string>();
  for (const m of matches.filter((m) => m.matched === top)) {
    for (const other of m.notSeparableFrom.filter((slug) => tied.includes(slug))) {
      const key = [m.slug, other].sort().join("|");
      if (namedPairs.has(key)) continue;
      namedPairs.add(key);
      const [a, b] = key.split("|");
      caveats.push(
        `${commonName(a)} and ${commonName(b)} are not reliably told apart by eye. Their published identification notes say so directly.`,
      );
    }
  }

  return { matches, answered, impliedStage, caveats };
}

function commonName(slug: string): string {
  return BY_SLUG.get(slug)?.commonName ?? slug;
}
